import { Bot, Check, X } from 'lucide-react';
import { ConfirmButton } from '@/shared/components/confirm-button';
import { IconTile } from '@/shared/components/icon-tile';
import { useTranslation } from '@/shared/i18n/use-translation';
import { cn } from '@/shared/lib/cn';
import { formatAgo } from '@/shared/lib/dates/format-ago';
import { Button } from '@/shared/ui/button/button';
import type { AccessRequest } from '../schemas/access-request';

type AccessRequestRowProps = {
  readonly request: AccessRequest;
  readonly highlighted: boolean;
  readonly onDecide: (decision: 'approve' | 'deny') => Promise<void>;
};

export function AccessRequestRow({ request, highlighted, onDecide }: AccessRequestRowProps) {
  const { t } = useTranslation();
  const minutesLeft = Math.max(0, Math.ceil((new Date(request.expires_at).getTime() - Date.now()) / 60_000));
  return (
    <div
      className={cn(
        'flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center',
        highlighted && 'bg-primary/5 ring-2 ring-primary ring-inset',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <IconTile icon={Bot} />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate font-medium">{request.client_name}</span>
          <span className="font-mono text-lg tracking-widest">{request.user_code}</span>
          <span className="text-sm text-muted-foreground">
            {t('Requested {ago} · expires in {minutes} min', {
              ago: formatAgo(request.created_at),
              minutes: minutesLeft,
            })}
          </span>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => void onDecide('deny')}>
          <X /> {t('Deny')}
        </Button>
        <ConfirmButton
          title={t('Give {name} access?', { name: request.client_name })}
          description={t(
            'It will be able to see and change the whole kit in your name for 30 days, until you revoke it. Check that the code {code} is the same one it shows you.',
            { code: request.user_code },
          )}
          confirmLabel={t('Allow')}
          destructive={false}
          onConfirm={() => onDecide('approve')}
          trigger={
            <Button className="flex-1 sm:flex-none">
              <Check /> {t('Allow')}
            </Button>
          }
        />
      </div>
    </div>
  );
}
